import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPendingTunnelEnrollment } from '@terra/ui-design-system';
import { internalNetworkEnrollmentApi } from '../../clients/tester-client/internal-network-enrollment-api';
import { INTERNAL_NETWORK_DISCOVERY_READINESS_KEY } from './useInternalNetworkDiscoveryStatus';
import { INTERNAL_NETWORK_ENROLLMENTS_KEY } from './useSurfaceSettings';

export const INTERNAL_NETWORK_TUNNEL_ENROLLMENTS_KEY =
  'internal-network-tunnel-enrollments';
export const INTERNAL_NETWORK_DEPLOYMENT_REQUIREMENTS_KEY =
  'internal-network-deployment-requirements';
export const INTERNAL_NETWORK_SENSOR_RELEASE_KEY =
  'internal-network-sensor-release';

const PENDING_TUNNEL_POLL_MS = 15_000;
const DEPLOYMENT_METADATA_STALE_TIME_MS = 60_000;

const requireNetworkId = (networkId: string | null): string => {
  if (!networkId) {
    throw new Error('Network ID is required');
  }

  return networkId;
};

const useInvalidateConnectionQueries = ({
  networkId,
  listKey,
}: {
  networkId: string | null;
  listKey: string;
}) => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [listKey, networkId] }),
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_DISCOVERY_READINESS_KEY, networkId],
      }),
    ]);
};

export function useInternalNetworkDeploymentRequirements() {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_DEPLOYMENT_REQUIREMENTS_KEY],
    queryFn: () =>
      internalNetworkEnrollmentApi.getInternalNetworkDeploymentRequirements(),
    staleTime: DEPLOYMENT_METADATA_STALE_TIME_MS,
  });
}

export function useLatestInternalNetworkSensorRelease({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_SENSOR_RELEASE_KEY],
    queryFn: () =>
      internalNetworkEnrollmentApi.getLatestInternalNetworkSensorRelease(),
    enabled,
    staleTime: DEPLOYMENT_METADATA_STALE_TIME_MS,
  });
}

export function useInternalNetworkTunnelEnrollments({
  networkId,
  enabled = true,
}: {
  networkId: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_TUNNEL_ENROLLMENTS_KEY, networkId],
    queryFn: () =>
      internalNetworkEnrollmentApi.listInternalNetworkTunnelEnrollments(
        networkId ?? '',
      ),
    enabled: Boolean(networkId) && enabled,
    refetchInterval: (query) =>
      hasPendingTunnelEnrollment(query.state.data?.data ?? [])
        ? PENDING_TUNNEL_POLL_MS
        : false,
  });
}

export function useCreateInternalNetworkEnrollment(networkId: string | null) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: () =>
      internalNetworkEnrollmentApi.createInternalNetworkEnrollment(
        requireNetworkId(networkId),
      ),
    onSuccess: () => invalidate(),
  });
}

export function useRevokeInternalNetworkEnrollment(networkId: string | null) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: (enrollmentId: string) =>
      internalNetworkEnrollmentApi.revokeInternalNetworkEnrollment({
        networkId: requireNetworkId(networkId),
        enrollmentId,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useRotateInternalNetworkEnrollmentKey(
  networkId: string | null,
) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: (enrollmentId: string) =>
      internalNetworkEnrollmentApi.rotateInternalNetworkEnrollmentKey({
        networkId: requireNetworkId(networkId),
        enrollmentId,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useCreateInternalNetworkTunnelEnrollment(
  networkId: string | null,
) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_TUNNEL_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: ({ advertisedRoutes }: { advertisedRoutes?: string[] }) =>
      internalNetworkEnrollmentApi.createInternalNetworkTunnelEnrollment({
        networkId: requireNetworkId(networkId),
        advertisedRoutes,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useReprovisionInternalNetworkTunnelEnrollment(
  networkId: string | null,
) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_TUNNEL_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: (enrollmentId: string) =>
      internalNetworkEnrollmentApi.reprovisionInternalNetworkTunnelEnrollmentArtifacts(
        { networkId: requireNetworkId(networkId), enrollmentId },
      ),
    onSuccess: () => invalidate(),
  });
}

export function useRevokeInternalNetworkTunnelEnrollment(
  networkId: string | null,
) {
  const invalidate = useInvalidateConnectionQueries({
    networkId,
    listKey: INTERNAL_NETWORK_TUNNEL_ENROLLMENTS_KEY,
  });

  return useMutation({
    mutationFn: (enrollmentId: string) =>
      internalNetworkEnrollmentApi.revokeInternalNetworkTunnelEnrollment({
        networkId: requireNetworkId(networkId),
        enrollmentId,
      }),
    onSuccess: () => invalidate(),
  });
}
